import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2, Globe, Server, ShieldCheck, Smartphone, Network, FileText, Layers, CreditCard, CalendarDays, Boxes, ShieldAlert, HeartPulse, Download, AlertTriangle, Search, Check, X, Sparkles, Stethoscope, Lock, Rocket, Wrench, Share2, Eye, Copy } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ScoreGauge from "@/components/ScoreGauge";
import GatedReport from "@/components/GatedReport";
import MigrationPitch from "@/components/MigrationPitch";
import AIAdvisor from "@/components/AIAdvisor";
import SecurityHeadersFix from "@/components/SecurityHeadersFix";
import AutoSeoCard from "@/components/AutoSeoCard";
import CloudflareApply from "@/components/CloudflareApply";
import FixGuide from "@/components/FixGuide";
import EmergentBuildPromo from "@/components/EmergentBuildPromo";
import { useWorkspace } from "@/context/WorkspaceContext";
import { FIX_GUIDES } from "@/lib/remediations";
import api from "@/lib/api";

const Row = ({ k, v, mono = true }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-0">
    <span className="text-zinc-500 text-sm shrink-0">{k}</span>
    <span className={`text-right text-zinc-200 text-sm break-all ${mono ? "font-mono" : ""}`}>
      {v == null || v === "" ? <span className="text-zinc-600">—</span> : v}
    </span>
  </div>
);

const Yes = ({ ok, on = "Yes", off = "No" }) => (
  <span className={ok ? "text-[#32D74B]" : "text-[#FF3B30]"}>{ok ? on : off}</span>
);

const timeAgo = (iso) => {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Chips = ({ items, empty = "None detected", color = "#4da3ff" }) => (
  <div className="flex flex-wrap gap-1.5">
    {items && items.length ? (
      items.map((t) => (
        <span key={t} className="font-mono text-[11px] px-2 py-0.5 rounded border" style={{ color, background: `${color}14`, borderColor: `${color}33` }}>{t}</span>
      ))
    ) : (
      <span className="text-zinc-600 text-sm">{empty}</span>
    )}
  </div>
);

const SEV = { critical: "#FF3B30", high: "#FF7A45", warning: "#FFD60A", info: "#4da3ff" };

const SeoChip = ({ ok, label }) => (
  <span
    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border"
    style={{ borderColor: ok ? "#30D15840" : "#FF3B3040", background: ok ? "#30D15812" : "#FF3B3012", color: ok ? "#30D158" : "#FF6961" }}
  >
    {ok ? <Check size={12} /> : <X size={12} />} {label}
  </span>
);


const Panel = ({ icon: Icon, title, children, blocked }) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-[#4da3ff]" />
      <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">{title}</h3>
    </div>
    {blocked && (
      <div className="flex items-start gap-2 mb-3 rounded-lg border border-[#FFD60A]/30 bg-[#FFD60A]/10 px-3 py-2" data-testid="panel-blocked-note">
        <AlertTriangle size={13} className="text-[#FFD60A] mt-0.5 shrink-0" />
        <span className="text-[#FFD60A] text-xs leading-relaxed">Not available — the site blocked the scanner from reading its page content. Results below may be empty or incomplete.</span>
      </div>
    )}
    {children}
  </div>
);

export default function DomainDetail() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const { active } = useWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [access, setAccess] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [headerDiff, setHeaderDiff] = useState(null);
  const [autoSeo, setAutoSeo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/scans/${encodeURIComponent(domain)}`);
      const first = res.data.history?.[0];
      setData(first?.result || null);
      setSummary(first?.exec_summary || null);
      setAutoSeo(first?.auto_seo || null);
      const acc = await api.get(`/domains/${encodeURIComponent(domain)}/access`).then((r) => r.data).catch(() => ({ unlocked: true }));
      setAccess(acc);
      if (acc?.unlocked) {
        api.get(`/report/${encodeURIComponent(domain)}/share`).then((r) => setShareInfo(r.data)).catch(() => {});
        api.get(`/domains/${encodeURIComponent(domain)}/header-diff`).then((r) => setHeaderDiff(r.data)).catch(() => {});
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [domain]);

  const generateSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get(`/summary/${encodeURIComponent(domain)}`);
      setSummary(res.data.summary);
      if (!res.data.cached) toast.success("Executive summary generated");
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("AI Executive Summary is a Pro feature."); navigate("/pricing"); }
      else toast.error(e?.response?.status === 404 ? "Scan the domain first." : "Could not generate the summary right now.");
    } finally { setSummaryLoading(false); }
  };

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe one-time checkout (?session_id=...) — poll until paid, then unlock.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    let cancelled = false;
    const clean = () => window.history.replaceState({}, "", `/domain/${encodeURIComponent(domain)}`);
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Payment complete — your full report is unlocked!");
          clean(); load(); return;
        }
        if (r.data.status === "expired") { toast.error("Payment session expired."); clean(); return; }
      } catch { /* keep polling */ }
      if (tries++ < 6) setTimeout(poll, 2000);
      else clean();
    };
    poll();
    return () => { cancelled = true; };
  }, [domain, load]);

  const purchase = async (productId) => {
    try {
      const res = await api.post("/checkout/product", { product_id: productId, domain, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const subscribeCare = async () => {
    try {
      const res = await api.post("/checkout", { plan_id: "pro_monthly", origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Could not start checkout");
    } catch { toast.error("Checkout failed"); }
  };

  const shareReport = async () => {
    try {
      const res = await api.post(`/report/${encodeURIComponent(domain)}/share`);
      setShareInfo(res.data);
      const url = `${window.location.origin}/api/public/report/${res.data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied — anyone with it can view this report (read-only)."); }
      catch { toast.success("Share link ready", { description: url }); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    }
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      const res = await api.post("/scan", { domain });
      setData(res.data);
      setSummary(null);
      toast.success("Rescanned");
    } catch { toast.error("Rescan failed"); }
    finally { setRescanning(false); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/report/${encodeURIComponent(domain)}`, { responseType: "blob" });
      const ct = res.headers?.["content-type"] || "";
      const isPdf = ct.includes("pdf");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${domain}-security-report.${isPdf ? "pdf" : "html"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success(isPdf ? "PDF report downloaded — open it to view or print." : "Report downloaded — open it to view or print to PDF.");
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(e?.response?.data?.detail || "Unlock this website to download the report."); navigate("/pricing"); }
      else toast.error(e?.response?.status === 404 ? "Scan the domain first." : "Report failed");
    }
  };

  const exportPackage = async () => {
    try {
      const res = await api.get(`/export/${encodeURIComponent(domain)}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `domainscope-handoff-${domain}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Handoff package downloaded" + (res.data.buildBrief ? " (includes build brief)" : " — generate a Build Brief to include the plan"));
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(e?.response?.data?.detail || "Unlock this website to export the package."); navigate("/pricing"); }
      else toast.error(e?.response?.status === 404 ? "Scan the domain first." : "Export failed");
    }
  };

  if (loading) return <Layout><div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#007AFF]" /></div></Layout>;
  if (!data) return <Layout><div className="text-center py-24 text-zinc-400">No scan data. <button onClick={rescan} className="text-[#4da3ff] underline">Scan now</button></div></Layout>;

  const s = data.scores || {};
  const fe = data.frontend || {};
  const sec = data.security || {};
  const mob = data.mobile || {};
  const ipi = data.ipInfo || {};
  const whois = data.whois || {};
  const dns = data.dns || {};
  const stack = data.stack || {};
  const integ = data.integrations || {};
  const dp = integ.dataPoints || {};
  const comp = data.compliance || {};
  const hc = data.healthcare || {};
  const hipaaMode = active?.hipaa_mode || "auto";
  const showHipaa = hipaaMode === "on" || (hipaaMode === "auto" && hc.isHealthcare);
  const co = data.cohosted || {};
  const seo = data.seo || {};
  const services = data.detectedServices || [];
  const access_ = data.access || {};
  const contentBlocked = data.contentAvailable === false;

  // ---- Gated (free) view: extracted to <GatedReport /> ----
  if (access && !access.unlocked) {
    return <GatedReport data={data} scores={s} comp={comp} sec={sec} seo={seo} onPurchase={purchase} />;
  }

  return (
    <Layout>
      <button data-testid="back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-5 transition-colors">
        <ArrowLeft size={16} /> Back to dashboard
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-white">{data.domain}</h1>
          <p className="font-mono text-sm text-zinc-500 mt-1">
            {data.reachable
              ? (access_.blocked
                  ? <span className="text-[#FFD60A]">● blocked ({data.statusCode})</span>
                  : <span className="text-[#32D74B]">● reachable ({data.statusCode})</span>)
              : <span className="text-[#FF3B30]">● unreachable</span>}
            {"  ·  "}{data.finalUrl}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-report-btn" onClick={downloadReport} className="btn-brand text-white rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2">
            <FileText size={15} /> Download Report
          </button>
          <button data-testid="share-report-btn" onClick={shareReport} className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2">
            <Share2 size={15} /> Share
            {shareInfo?.enabled && shareInfo?.views > 0 && (
              <span data-testid="share-views-count" className="ml-1 inline-flex items-center gap-1 text-[11px] text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/25 rounded-full px-1.5 py-0.5">
                <Eye size={11} /> {shareInfo.views}
              </span>
            )}
          </button>
          <button data-testid="export-package-btn" onClick={exportPackage} className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2">
            <Download size={15} /> Export JSON
          </button>
          <button data-testid="rescan-btn" onClick={rescan} disabled={rescanning} className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2">
            {rescanning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Rescan
          </button>
        </div>
      </div>

      {access_.message && (
        <div
          data-testid="access-warning-banner"
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-6 ${access_.blocked ? "border-[#FF7A45]/40 bg-[#FF7A45]/10" : "border-[#FFD60A]/30 bg-[#FFD60A]/10"}`}
        >
          <AlertTriangle size={18} className={access_.blocked ? "text-[#FF7A45] mt-0.5 shrink-0" : "text-[#FFD60A] mt-0.5 shrink-0"} />
          <div>
            <p className={`text-sm font-semibold ${access_.blocked ? "text-[#FF7A45]" : "text-[#FFD60A]"}`}>
              {access_.blocked ? "Content blocked by the site" : "Limited content retrieved"}
            </p>
            <p className="text-zinc-300 text-sm mt-0.5 leading-relaxed">{access_.message}</p>
            <p className="text-zinc-500 text-xs mt-1">DNS, hosting/IP, SSL, security headers and WHOIS below are still accurate — they don't depend on reading the page.</p>
          </div>
        </div>
      )}

      {/* AI Executive Summary */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#141827] to-[#0f1119] border border-[#4da3ff]/25 rounded-xl p-5 mb-6" data-testid="exec-summary-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#4da3ff]/10 blur-3xl pointer-events-none" />
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#4da3ff]" />
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">AI Executive Summary</h3>
          </div>
          {summary && (
            <button
              data-testid="regenerate-summary-btn"
              onClick={generateSummary}
              disabled={summaryLoading}
              className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5 transition-colors"
            >
              {summaryLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate
            </button>
          )}
        </div>
        {summary ? (
          <p data-testid="exec-summary-text" className="text-zinc-200 text-sm leading-relaxed whitespace-pre-line">{summary}</p>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-zinc-400 text-sm leading-relaxed max-w-2xl">
              Turn this scan into a plain-English, consultant-level summary of what your site is built on, the risks found, and the modernization opportunity — perfect for sharing with a non-technical stakeholder.
            </p>
            <button
              data-testid="generate-summary-btn"
              onClick={generateSummary}
              disabled={summaryLoading}
              className="btn-brand text-white rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {summaryLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {summaryLoading ? "Generating…" : "Generate Executive Summary"}
            </button>
          </div>
        )}
      </div>

      {/* Guided Fix ($99) progress tracker */}
      {access?.fix_purchased && (() => {
        const steps = [
          { key: "requested", label: "Requested", desc: "We've received your Guided Fix order" },
          { key: "in_progress", label: "In progress", desc: "Our team is implementing the fixes" },
          { key: "done", label: "Done", desc: "Fixes completed & delivered" },
        ];
        const cur = access.fix_status || "requested";
        const curIdx = Math.max(0, steps.findIndex((s) => s.key === cur));
        const isComplete = cur === "done";
        const histAt = (k) => ((access.fix_status_history || []).find((x) => x.status === k) || {}).at;
        return (
          <div data-testid="fix-progress-tracker" className="rounded-xl border border-[#32D74B]/25 bg-[#0f1512] p-5 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <Wrench size={16} className="text-[#32D74B]" />
              <h3 className="font-heading font-semibold text-white text-sm">Guided Fix — progress</h3>
              <span className="ml-auto text-xs text-zinc-500">$99 done-for-you</span>
            </div>
            <div className="flex items-start">
              {steps.map((s, i) => {
                const done = i < curIdx || (isComplete && i === curIdx);
                const active = i === curIdx && !done;
                const at = histAt(s.key);
                return (
                  <div key={s.key} className="flex-1 flex flex-col items-center relative">
                    {i > 0 && <div className={`absolute top-3 right-1/2 w-full h-0.5 ${i <= curIdx ? "bg-[#32D74B]" : "bg-white/10"}`} />}
                    <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-[#32D74B] text-black" : active ? "bg-[#32D74B]/20 border-2 border-[#32D74B] text-[#32D74B]" : "bg-white/5 border border-white/15 text-zinc-500"}`}>
                      {done ? <Check size={13} /> : i + 1}
                    </div>
                    <div className="mt-2 text-center px-1">
                      <div className={`text-xs font-medium ${i <= curIdx ? "text-white" : "text-zinc-500"}`} data-testid={`fix-step-${s.key}`}>{s.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight hidden sm:block">{s.desc}</div>
                      {at && <div className="text-[10px] text-[#32D74B] mt-0.5">{new Date(at).toLocaleDateString()}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Detected host + migration pitch */}
      <MigrationPitch data={data} comp={comp} seo={seo} sec={sec} />

      {/* Post-purchase upsell (one-time buyers on the free plan) */}
      {access?.plan === "free" && access?.unlocked && (
        <div data-testid="post-purchase-upsell" className="relative overflow-hidden rounded-xl border border-[#9b5cff]/30 p-5 mb-6" style={{ background: "linear-gradient(135deg, rgba(0,122,255,0.12), rgba(155,92,255,0.12))" }}>
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#9b5cff]/10 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-3">
            <Sparkles size={18} className="text-[#c9a6ff] mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-white text-base">You've got the plan — want it handled for you?</h3>
              <p className="text-zinc-400 text-sm mt-1 leading-relaxed">Skip the DIY work and let us implement the fixes, or never worry about this site again with the ongoing Pro plan.</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <button data-testid="upsell-fix-btn" onClick={() => purchase("guided_fix")} className="btn-brand text-white rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2">
                  <Wrench size={15} /> Have us fix it — from $99
                </button>
                <button data-testid="upsell-care-btn" onClick={subscribeCare} className="bg-transparent border border-white/25 text-white hover:bg-white/10 rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
                  <HeartPulse size={15} /> Never worry again — Pro $19.99/mo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scores */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-6 place-items-center">
          <ScoreGauge value={s.frontend} label="Frontend" />
          <ScoreGauge value={s.mobile} label="Mobile" />
          <ScoreGauge value={s.security} label="Security" />
          <ScoreGauge value={s.seo} label="SEO" />
          <ScoreGauge value={s.compliance} label="Compliance" />
          <ScoreGauge value={s.integration} label="Readiness" />
        </div>
      </div>

      {/* Security headers — actionable fix to 100 */}
      <SecurityHeadersFix security={sec} />

      {/* One-Click Apply via Cloudflare */}
      <CloudflareApply domain={data.domain} />

      {/* Security header changes since the previous scan */}
      {headerDiff && headerDiff.assessed && (headerDiff.added?.length || headerDiff.removed?.length || headerDiff.changed?.length) ? (
        <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="header-diff-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Security header changes since last scan</h3>
            <span className="text-[11px] text-zinc-500">vs previous scan</span>
          </div>
          <div className="space-y-2">
            {headerDiff.added?.map((h, i) => (
              <div key={`a${i}`} className="flex items-start gap-2 text-xs" data-testid="header-diff-added">
                <span className="text-emerald-400 font-semibold">+ Added</span>
                <span className="text-zinc-300">{h.header}</span>
                {h.to && h.to !== "present" && <span className="text-zinc-500 truncate">→ {h.to}</span>}
              </div>
            ))}
            {headerDiff.removed?.map((h, i) => (
              <div key={`r${i}`} className="flex items-start gap-2 text-xs" data-testid="header-diff-removed">
                <span className="text-red-400 font-semibold">− Removed</span>
                <span className="text-zinc-300">{h.header}</span>
              </div>
            ))}
            {headerDiff.changed?.map((h, i) => (
              <div key={`c${i}`} className="flex items-start gap-2 text-xs" data-testid="header-diff-changed">
                <span className="text-amber-400 font-semibold">~ Changed</span>
                <span className="text-zinc-300">{h.header}</span>
                <span className="text-zinc-500 truncate">{h.from} → {h.to}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* AI-generated copy-paste SEO fixes */}
      <AutoSeoCard domain={data.domain} initial={autoSeo} seoResult={data.seo} platform={data.hosting?.platform || data.stack?.cms} />

      {/* Privacy & Compliance */}
      <div className="bg-[#121212] border border-[#FF3B30]/25 rounded-xl p-5 mb-6" data-testid="compliance-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <HeartPulse size={16} className="text-[#FF3B30]" />
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Privacy & Compliance</h3>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs">
            {["critical", "high", "warning", "info"].map((k) => (
              (comp.counts?.[k] || 0) > 0 && (
                <span key={k} style={{ color: SEV[k] }}>● {comp.counts[k]} {k}</span>
              )
            ))}
          </div>
        </div>
        {contentBlocked && (
          <div className="flex items-start gap-2 mb-3 rounded-lg border border-[#FFD60A]/30 bg-[#FFD60A]/10 px-3 py-2" data-testid="compliance-blocked-note">
            <AlertTriangle size={13} className="text-[#FFD60A] mt-0.5 shrink-0" />
            <span className="text-[#FFD60A] text-xs leading-relaxed">The site blocked page-content access, so tracker/pixel and form privacy checks could not run. Header &amp; TLS checks below are still valid.</span>
          </div>
        )}
        {comp.flags && comp.flags.length ? (
          <div className="space-y-2">
            {comp.flags.map((f) => (
              <div key={f.id} className="border border-white/10 rounded-lg p-3.5" data-testid={`flag-${f.id}`} style={{ background: `${SEV[f.severity]}0a` }}>
                <div className="flex items-center gap-2">
                  <ShieldAlert size={14} style={{ color: SEV[f.severity] }} />
                  <span className="text-white text-sm font-medium">{f.title}</span>
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[f.severity], background: `${SEV[f.severity]}1a` }}>{f.severity}</span>
                </div>
                <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">{f.detail}</p>
                <p className="text-[#32D74B] text-xs mt-1.5 leading-relaxed"><span className="font-semibold">Fix:</span> {f.remediation}</p>
                <FixGuide guide={FIX_GUIDES[f.id]} testId={`fix-guide-${f.id}`} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#32D74B] text-sm">{contentBlocked ? "No header/TLS issues found (content checks unavailable)." : "No compliance issues detected."}</p>
        )}
        <p className="text-zinc-600 text-[11px] mt-3">Engineering guidance, not legal advice.</p>
      </div>

      {/* Healthcare / HIPAA Website Risk — controlled by workspace setting */}
      {showHipaa && (
      <div className="bg-[#121212] border border-[#30D158]/25 rounded-xl p-5 mb-6" data-testid="healthcare-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Stethoscope size={16} className="text-[#30D158]" />
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Healthcare / HIPAA Website Risk</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ color: hc.riskLevel === "high" ? SEV.critical : hc.riskLevel === "medium" ? SEV.warning : "#30D158",
                       background: `${hc.riskLevel === "high" ? SEV.critical : hc.riskLevel === "medium" ? SEV.warning : "#30D158"}1a` }}>
              {hc.riskLevel || "low"} risk
            </span>
            <span className="font-heading text-lg font-bold text-white tabular-nums" data-testid="healthcare-score">{hc.score ?? "—"}<span className="text-zinc-600 text-xs">/100</span></span>
          </div>
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed mb-3">
          For medical, dental, therapy, rehab &amp; any health-related practice. Checks the public website for the tracking &amp; transport risks that trigger HHS/OCR HIPAA enforcement — <b>no patient data is accessed or stored.</b>
        </p>
        {hc.findings && hc.findings.length ? (
          <div className="space-y-2">
            {hc.findings.map((f) => (
              <div key={f.id} className="border border-white/10 rounded-lg p-3.5" data-testid={`hipaa-flag-${f.id}`} style={{ background: `${SEV[f.severity]}0a` }}>
                <div className="flex items-center gap-2">
                  <ShieldAlert size={14} style={{ color: SEV[f.severity] }} />
                  <span className="text-white text-sm font-medium">{f.title}</span>
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[f.severity], background: `${SEV[f.severity]}1a` }}>{f.severity}</span>
                </div>
                <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">{f.detail}</p>
                <p className="text-[#32D74B] text-xs mt-1.5 leading-relaxed"><span className="font-semibold">Fix:</span> {f.remediation}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#32D74B] text-sm">No HIPAA website-risk signals detected.</p>
        )}
        <p className="text-zinc-600 text-[11px] mt-3">A website-risk assessment, not legal advice or a HIPAA compliance certification.</p>
      </div>
      )}

      {/* SEO */}
      <div className="bg-[#121212] border border-[#30D158]/25 rounded-xl p-5 mb-6" data-testid="seo-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-[#30D158]" />
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">SEO</h3>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs">
            {["critical", "high", "warning", "info"].map((k) => (
              (seo.counts?.[k] || 0) > 0 && (
                <span key={k} style={{ color: SEV[k] }}>● {seo.counts[k]} {k}</span>
              )
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <SeoChip ok={!!seo.title} label="Title" />
          <SeoChip ok={!!seo.description} label="Meta description" />
          <SeoChip ok={seo.h1Count === 1} label={`H1 (${seo.h1Count ?? 0})`} />
          <SeoChip ok={seo.hasCanonical} label="Canonical" />
          <SeoChip ok={seo.indexable} label="Indexable" />
          <SeoChip ok={seo.hasOpenGraph} label="Open Graph" />
          <SeoChip ok={seo.hasStructuredData} label="Structured data" />
          <SeoChip ok={seo.hasRobotsTxt} label="robots.txt" />
          <SeoChip ok={seo.hasSitemap} label="Sitemap" />
          <SeoChip ok={!!seo.lang} label="lang" />
        </div>
        {contentBlocked && (
          <div className="flex items-start gap-2 mb-3 rounded-lg border border-[#FFD60A]/30 bg-[#FFD60A]/10 px-3 py-2">
            <AlertTriangle size={13} className="text-[#FFD60A] mt-0.5 shrink-0" />
            <span className="text-[#FFD60A] text-xs leading-relaxed">The site blocked page-content access, so most SEO checks could not run.</span>
          </div>
        )}
        {seo.flags && seo.flags.length ? (
          <div className="space-y-2">
            {seo.flags.map((f) => (
              <div key={f.id} className="border border-white/10 rounded-lg p-3.5" data-testid={`seo-flag-${f.id}`} style={{ background: `${SEV[f.severity]}0a` }}>
                <div className="flex items-center gap-2">
                  <Search size={14} style={{ color: SEV[f.severity] }} />
                  <span className="text-white text-sm font-medium">{f.title}</span>
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[f.severity], background: `${SEV[f.severity]}1a` }}>{f.severity}</span>
                </div>
                <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">{f.detail}</p>
                <p className="text-[#32D74B] text-xs mt-1.5 leading-relaxed"><span className="font-semibold">Fix:</span> {f.remediation}</p>
                <FixGuide guide={FIX_GUIDES[f.id]} testId={`seo-fix-guide-${f.id}`} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#32D74B] text-sm">No SEO issues detected.</p>
        )}
      </div>

      {/* Detected services — explained */}
      {services.length > 0 && (
        <div className="bg-[#121212] border border-[#4da3ff]/25 rounded-xl p-5 mb-6" data-testid="detected-services-panel">
          <div className="flex items-center gap-2 mb-1">
            <Network size={18} className="text-[#4da3ff]" />
            <h2 className="font-heading text-lg font-semibold text-white">Detected Services — Explained</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-4">Every technology and third-party script we identified on this site, and what each one does.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {services.map((s, i) => {
              const unknown = s.category === "Third-party script" && /\./.test(s.name);
              const color = unknown ? "#FFD60A" : "#4da3ff";
              return (
                <div key={s.name + i} data-testid={`service-${i}`} className="rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium break-all">{s.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color, background: `${color}1a` }}>{s.category}</span>
                  </div>
                  <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Panel icon={Layers} title="Full Stack" blocked={contentBlocked}>
          <Row k="CMS" v={stack.cms} />
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Analytics</p><Chips items={stack.analytics} /></div>
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Tracking pixels</p><Chips items={stack.trackingPixels} color="#FF3B30" empty="None" /></div>
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Chat widgets</p><Chips items={stack.chatWidgets} empty="None" /></div>
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Fonts</p><Chips items={stack.fonts} empty="None" /></div>
          <Row k="3rd-party origins" v={stack.thirdPartyCount} />
        </Panel>

        <Panel icon={CreditCard} title="Detected Services · Payments · Booking" blocked={contentBlocked}>
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Payments</p><Chips items={integ.payments} color="#32D74B" empty="None detected" /></div>
          <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Booking / Scheduling</p><Chips items={integ.booking} empty="None detected" /></div>
          {integ.ehr?.length ? (
            <div className="py-2 border-b border-white/5"><p className="text-zinc-500 text-sm mb-1.5">Patient portal / EHR</p><Chips items={integ.ehr} color="#FFD60A" empty="None detected" /></div>
          ) : null}
          <Row k="Forms on page" v={dp.formCount} />
          <Row k="Collects email" v={<Yes ok={dp.collectsEmail} />} />
          <Row k="Collects phone" v={<Yes ok={dp.collectsPhone} />} />
          <Row k="File upload" v={<Yes ok={dp.fileUpload} />} />
          <Row k="Insecure (HTTP) forms" v={<span className={dp.insecureForms ? "text-[#FF3B30]" : "text-[#32D74B]"}>{dp.insecureForms || 0}</span>} />
        </Panel>

        <Panel icon={Globe} title="Frontend Tech" blocked={contentBlocked}>
          <Row k="Framework" v={fe.framework} />
          <Row k="React" v={<Yes ok={fe.usesReact} />} />
          <Row k="React version" v={fe.reactVersion} />
          <Row k="Next.js" v={<Yes ok={fe.usesNext} />} />
          <Row k="Vue" v={<Yes ok={fe.usesVue} />} />
          <Row k="Angular" v={<Yes ok={fe.usesAngular} />} />
          <Row k="jQuery" v={<Yes ok={fe.usesjQuery} />} />
          <Row k="Bundler" v={fe.bundler} />
          <Row k="Generator" v={fe.generator} />
        </Panel>

        <Panel icon={Server} title="Hosting · IP · Host">
          <Row k="IP address" v={data.ip} />
          <Row k="Host / Org" v={ipi.org || ipi.isp} />
          <Row k="ISP" v={ipi.isp} />
          <Row k="ASN" v={ipi.as} />
          <Row k="Location" v={[ipi.city, ipi.regionName, ipi.country].filter(Boolean).join(", ")} />
          <Row k="CDN" v={data.cdn} />
          <Row k="Server header" v={sec.server} />
          <Row k="Powered by" v={sec.poweredBy} />
        </Panel>

        <Panel icon={Boxes} title="Co-hosted Domains (same IP)">
          <Row k="Provider" v={co.provider} />
          <Row k="Domains on IP" v={co.count} />
          {co.note && <Row k="Note" v={co.note} />}
          <div className="pt-2 max-h-56 overflow-y-auto">
            {(co.domains || []).length ? (
              <div className="flex flex-col gap-1">
                {co.domains.map((d) => (
                  <span key={d} className="font-mono text-xs text-zinc-300 truncate">{d}</span>
                ))}
              </div>
            ) : <span className="text-zinc-600 text-sm">No co-hosted domains found.</span>}
          </div>
        </Panel>

        <Panel icon={ShieldCheck} title="Security & SSL">
          <Row k="HTTPS available" v={<Yes ok={sec.https} />} />
          <Row k="TLS certificate valid" v={sec.tlsCertValid ? <Yes ok={true} /> : (sec.tlsAvailable ? <span className="text-[#FF9F0A] text-xs">Chain/verify issue</span> : <Yes ok={false} />)} />
          {sec.certErrorType && <Row k="Certificate issue" v={<span className="text-[#FF9F0A] text-xs">{String(sec.certErrorType).replace(/-/g, " ")}</span>} />}
          <Row k="HTTP → HTTPS redirect" v={sec.httpsRedirect == null ? "—" : <Yes ok={sec.httpsRedirect} on="Yes" off="No" />} />
          <Row k="SSL issuer" v={data.ssl?.issuer} />
          <Row k="SSL expires (days)" v={data.ssl?.days_left} />
          {access && (
            <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 text-[11px]" data-testid="cert-monitoring-note">
              <span className={`h-1.5 w-1.5 rounded-full ${access.monitored ? "bg-[#32D74B] animate-pulse" : "bg-zinc-500"}`} />
              {access.monitored ? (
                <span className="text-zinc-400">
                  {access.cert_alert_state
                    ? <span className="text-amber-400">Certificate flagged — we alerted you. </span>
                    : <span className="text-[#32D74B]">SSL monitoring active. </span>}
                  Last checked {timeAgo(access.last_checked_at)}.
                </span>
              ) : (
                <span className="text-zinc-500">Last scanned {timeAgo(access.last_checked_at)}. Upgrade to Pro for daily SSL monitoring &amp; alerts.</span>
              )}
            </div>
          )}
          {sec.headersRead === false ? (
            <Row k="Security headers" v={<span className="text-zinc-500 text-xs">Not assessed — the site blocked our scanner</span>} />
          ) : (
            <>
              <Row k="HSTS" v={
                sec.hsts
                  ? <span className="inline-flex items-center gap-2" data-testid="hsts-grade">
                      <Yes ok={true} on="Enabled" />
                      {sec.hstsGrade && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                          sec.hstsGrade === "strong" ? "bg-emerald-500/15 text-emerald-400"
                          : sec.hstsGrade === "adequate" ? "bg-amber-500/15 text-amber-400"
                          : "bg-red-500/15 text-red-400"}`}>{sec.hstsGrade}</span>
                      )}
                    </span>
                  : <Yes ok={false} on="Enabled" off="Missing" />
              } />
              {sec.hsts && Array.isArray(sec.hstsIssues) && sec.hstsIssues.length > 0 && (
                <div className="text-[11px] text-amber-400/90 pl-1 -mt-1 space-y-1" data-testid="hsts-issues">
                  {sec.hstsIssues.map((it, i) => <div key={i}>• {it}</div>)}
                  <button
                    onClick={() => { navigator.clipboard?.writeText("Strict-Transport-Security: max-age=31536000; includeSubDomains; preload"); toast.success("Strong HSTS header copied"); }}
                    data-testid="hsts-fix-copy-btn"
                    className="mt-1 inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-md px-2.5 py-1 text-[11px] transition-colors">
                    <Copy size={11} /> Copy strong HSTS header
                  </button>
                </div>
              )}
              <Row k="CSP" v={<Yes ok={sec.csp} on="Enabled" off="Missing" />} />
              <Row k="CORS" v={sec.cors || "None"} />
              <Row k="X-Frame-Options" v={sec.xFrameOptions} />
              <Row k="Referrer-Policy" v={sec.referrerPolicy} />
            </>
          )}
        </Panel>

        <Panel icon={Smartphone} title="Mobile Compatibility" blocked={contentBlocked}>
          <Row k="Viewport tag" v={mob.viewportTag} />
          <Row k="Device-width" v={<Yes ok={mob.deviceWidth} />} />
          <Row k="Responsive" v={<Yes ok={mob.isResponsive} />} />
          <Row k="Media queries" v={mob.mediaQueries} />
        </Panel>

        <Panel icon={Network} title="DNS Records">
          {["A", "AAAA", "MX", "NS", "CNAME"].map((t) => (
            <Row key={t} k={t} v={(dns[t] || []).join(", ")} />
          ))}
        </Panel>

        <Panel icon={FileText} title="WHOIS / Registrar">
          <Row k="Registrar" v={whois.registrar} />
          <Row k="Org" v={whois.org} />
          <Row k="Country" v={whois.country} />
          <Row k="Created" v={whois.creation_date} />
          <Row k="Expires" v={whois.expiration_date} />
          <Row k="Name servers" v={(whois.name_servers || []).join(", ")} />
        </Panel>
      </div>

      <EmergentBuildPromo domain={data.domain} />
      <AIAdvisor domains={[data.domain]} />
    </Layout>
  );
}
